// [ai] What /timeline and /life share: the `life` query (with the ?path/overrides/outline test seam forwarded), the empty page,
// the problems list, and the bridges into the player (play, folder summary, recording summary, the page sheets).
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { useAtlasQuery, type Params } from "../../api/query";
import { usePlayerCtl } from "../../player/context";
import type { Player } from "../../player/player";
import { usePageSeam } from "../../app/pageSeam";
import { fmtSpan, type Chapter, type Era, type Event, type LifeData, type Row } from "./index";
import { eventMd, type FolderRef } from "./render";

export function useLifeData() {
  const [sp] = useSearchParams();
  const params = useMemo(() => { const p: Params = {}; for (const k of ["path", "overrides", "outline"]) { const v = sp.get(k); if (v) p[k] = v; } return p; }, [sp]);
  return { q: useAtlasQuery<LifeData>("life", params, { staleTime: Infinity }), miscSec: +(sp.get("miscSec") || 0) || undefined };
}
/** No library or no dated recording: one line, and a seam that says so. */
export function EmptyLife({ data, name, cls, what }: { data: LifeData; name: string; cls: string; what: string }) {
  const text = data.problems?.includes("no library") ? `No library yet — nothing to ${what}.` : "No dated recordings yet.";
  usePageSeam(name, { dump: () => ({ empty: text, problems: data.problems }) });
  return <div className={cls}><div className={`${cls}-empty`}>{text}</div></div>;
}
export function Problems({ list, cls, of }: { list: string[]; cls: string; of: string }) {
  if (!list.length) return null;
  return <details className={cls}><summary>{list.length} problem{list.length === 1 ? "" : "s"} in the {of} files</summary><ul>{list.slice(0, 60).map((p, i) => <li key={i}>{p}</li>)}</ul></details>;
}
export function useBridge() {
  const p = usePlayerCtl();
  return useMemo(() => bridge(p), [p]);
}
export function bridge(p: Player) {
  return {
    play: (id: string, t: number) => void p.open({ kind: "rec", id, t: t || 0, e: null }, { play: true }),
    folder: (f: FolderRef) => void p.showFolderSummary({ path: f.folder, summary: "_FOLDER-SUMMARY.md", name: f.title, count: f.count, duration: (f.minutes || 0) * 60, children: [], items: [] }),
    summary: (r: Row) => void p.library().then(lib => { const it = lib?.items.find(i => i.id === r.id); if (it) void p.showSummary(it); else void p.open({ kind: "rec", id: r.id, t: 0, e: null }, { play: false }); }),
    event: (e: Event) => p.showSheet({ title: e.title, sub: `${e.kind} · ${fmtSpan(e.date, e.end, 3)}`, md: eventMd(e), kind: "event" }),
    era: (e: Era) => p.showSheet({ title: e.title, sub: `Era · ${fmtSpan(e.from, e.to, 1)}`, md: `*${e.line}*\n\n${e.summary || ""}`, kind: "era" }),
    chapter: (c: Chapter, span: string) => p.showSheet({ title: c.title, sub: `Chapter · ${span}`, md: `*${c.line}*\n\n${c.summary || ""}`, kind: "chapter" }),
    undated: (u: Row[]) => p.showSheet({ title: `${u.length} undated recordings`, sub: "Files without a date; they live in their folders", kind: "undated", md: u.map(x => `- [${x.short || x.title}](rec:${x.id}?t=0) — ${x.folder}`).join("\n") }),
  };
}
